import { useState, useEffect } from "react";
import { 
  Users, Building2, DollarSign, Calendar,
  Target, Clock, AlertCircle, CalendarDays, Trophy, 
  MousePointer, FileCheck, Package, TrendingUp, Home,
  ArrowUpRight, Percent, MapPin, HardHat
} from "lucide-react";
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import PlantaoModal from "../components/PlantaoModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODOS = [
  { value: 'dia', label: 'Hoje', days: 1 },
  { value: 'semana', label: 'Esta Semana', days: 7 },
  { value: 'mes', label: 'Este Mês', days: 30 },
  { value: 'trimestre', label: 'Trimestre', days: 90 },
  { value: 'semestre', label: 'Semestre', days: 180 },
  { value: 'ano', label: 'Este Ano', days: 365 }
];

const STATUS_EMPREENDIMENTO = {
  lancamento: { label: 'Lançamento', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  em_construcao: { label: 'Em Construção', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
  pronto: { label: 'Pronto', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  entregue: { label: 'Entregue', color: 'bg-slate-500', textColor: 'text-slate-700', bgLight: 'bg-slate-50' },
};

const Dashboard = ({ onLogout }) => {
  const [periodo, setPeriodo] = useState('mes');
  const [stats, setStats] = useState({
    totalClientes: 0, clientesNoPeriodo: 0,
    totalEmpreendimentos: 0, totalUnidades: 0,
    unidadesVendidas: 0, unidadesReservadas: 0, unidadesDisponiveis: 0,
    vgvTotal: 0, vgvVendido: 0,
    agendamentosHoje: 0, negociacoes: 0,
    vendasNoPeriodo: 0, vgvNoPeriodo: 0,
  });
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [unidadesPorStatus, setUnidadesPorStatus] = useState([]);
  const [clientesPorMes, setClientesPorMes] = useState([]);
  const [vendasPorMes, setVendasPorMes] = useState([]);
  const [rankingCorretores, setRankingCorretores] = useState({ vendas: [], interacoes: [] });
  const [loading, setLoading] = useState(true);
  const [showPlantaoModal, setShowPlantaoModal] = useState(false);
  const [avisos, setAvisos] = useState({ plantao: null, rodizio: null });
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    document.title = "InstaIMOB | CRM";
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
    fetchDashboardData();
    if (user?.role === 'proprietario' || user?.role === 'admin' || user?.role === 'gestor' || user?.role === 'diretor' || user?.role === 'diretor_comercial') {
      verificarAvisos();
    }
  }, [periodo]);

  const verificarAvisos = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/api/plantao/avisos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvisos(res.data);
      if (res.data?.plantao?.mostrar_aviso) setShowPlantaoModal(true);
    } catch (error) {
      console.error("Erro ao verificar avisos:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [clientesRes, empreendimentosRes, agendamentosRes, usersRes, unidadesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/clients`, { headers }),
        axios.get(`${BACKEND_URL}/api/empreendimentos`, { headers }),
        axios.get(`${BACKEND_URL}/api/agendamentos`, { headers }),
        axios.get(`${BACKEND_URL}/api/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/unidades?limit=9999`, { headers }).catch(() => ({ data: { items: [] } }))
      ]);

      const clientes = clientesRes.data || [];
      const emps = empreendimentosRes.data || [];
      const agendamentos = agendamentosRes.data || [];
      const usuarios = usersRes.data || [];
      const unidades = unidadesRes.data?.items || unidadesRes.data || [];

      setEmpreendimentos(emps);

      // Período
      const periodoConfig = PERIODOS.find(p => p.value === periodo);
      const diasAtras = periodoConfig?.days || 30;
      const dataInicio = new Date();
      dataInicio.setDate(dataInicio.getDate() - diasAtras);
      dataInicio.setHours(0, 0, 0, 0);

      const clientesNoPeriodo = clientes.filter(c => c.created_at && new Date(c.created_at) >= dataInicio);

      // Calcular unidades por status
      let totalUnidades = 0;
      let unidadesVendidas = 0;
      let unidadesReservadas = 0;
      let unidadesDisponiveis = 0;
      let vgvTotal = 0;
      let vgvVendido = 0;

      if (unidades.length > 0) {
        totalUnidades = unidades.length;
        unidades.forEach(u => {
          const st = (u.status || '').toLowerCase();
          const valor = u.valor || 0;
          vgvTotal += valor;
          if (st === 'vendida' || st === 'vendido') { unidadesVendidas++; vgvVendido += valor; }
          else if (st === 'reservada' || st === 'reservado') { unidadesReservadas++; }
          else { unidadesDisponiveis++; }
        });
      } else {
        // Sem unidades individuais — usar total_unidades dos empreendimentos
        emps.forEach(e => { totalUnidades += (e.total_unidades || 0); });
        unidadesDisponiveis = totalUnidades;
      }

      // Agendamentos hoje
      const hoje = new Date().toISOString().split('T')[0];
      const agendamentosHoje = agendamentos.filter(a => a.data_visita && a.data_visita.startsWith(hoje)).length;

      // Negociações (clientes em negociação)
      const negociacoes = clientes.filter(c => {
        const st = (c.status || '').toLowerCase();
        return st === 'negociacao' || st === 'proposta' || st === 'em_negociacao';
      }).length;

      setStats({
        totalClientes: clientes.length,
        clientesNoPeriodo: clientesNoPeriodo.length,
        totalEmpreendimentos: emps.length,
        totalUnidades, unidadesVendidas, unidadesReservadas, unidadesDisponiveis,
        vgvTotal, vgvVendido,
        agendamentosHoje, negociacoes,
        vendasNoPeriodo: 0, vgvNoPeriodo: 0,
      });

      // Gráfico pizza — Unidades por status
      const statusData = [];
      if (unidadesVendidas > 0) statusData.push({ name: 'Vendidas', value: unidadesVendidas, status: 'vendida' });
      if (unidadesReservadas > 0) statusData.push({ name: 'Reservadas', value: unidadesReservadas, status: 'reservada' });
      if (unidadesDisponiveis > 0) statusData.push({ name: 'Disponíveis', value: unidadesDisponiveis, status: 'disponivel' });
      setUnidadesPorStatus(statusData);

      // Gráfico leads por mês
      const meses = getMesesPorPeriodo(periodo);
      setClientesPorMes(getDataPorMes(clientes, 'created_at', meses));

      // Ranking corretores
      const usuariosMap = {};
      usuarios.forEach(u => { usuariosMap[u.id] = u.name || u.email || 'Corretor'; });

      const interacoesPorCorretor = {};
      clientes.forEach(cli => {
        const corretorId = cli.assigned_to || cli.created_by;
        if (corretorId) {
          if (!interacoesPorCorretor[corretorId]) interacoesPorCorretor[corretorId] = { id: corretorId, interacoes: 0, clientes: 0 };
          interacoesPorCorretor[corretorId].clientes += 1;
          interacoesPorCorretor[corretorId].interacoes += 1;
          if (cli.historico && Array.isArray(cli.historico)) interacoesPorCorretor[corretorId].interacoes += cli.historico.length;
        }
      });
      agendamentos.forEach(ag => {
        const corretorId = ag.corretor_id || ag.created_by;
        if (corretorId) {
          if (!interacoesPorCorretor[corretorId]) interacoesPorCorretor[corretorId] = { id: corretorId, interacoes: 0, clientes: 0 };
          interacoesPorCorretor[corretorId].interacoes += 1;
        }
      });

      setRankingCorretores({
        vendas: [],
        interacoes: Object.values(interacoesPorCorretor)
          .map(c => ({ ...c, nome: usuariosMap[c.id] || 'Corretor' }))
          .sort((a, b) => b.interacoes - a.interacoes)
          .slice(0, 5)
      });

    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMesesPorPeriodo = (p) => {
    switch(p) {
      case 'dia': return 7; case 'semana': return 4;
      case 'mes': return 6; case 'trimestre': return 6;
      case 'semestre': return 6; case 'ano': return 12; default: return 6;
    }
  };

  const getDataPorMes = (data, dateField, meses) => {
    const result = [];
    const now = new Date();
    for (let i = meses - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const mesAno = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
      const count = data.filter(item => {
        if (!item[dateField]) return false;
        const d = new Date(item[dateField]);
        return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
      }).length;
      result.push({ mes: mesAno, quantidade: count });
    }
    return result;
  };

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', {
    style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);

  const getStatusColor = (status) => ({
    disponivel: '#10b981', reservada: '#f59e0b', vendida: '#3b82f6',
  }[status] || '#94a3b8');

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50">
        <Sidebar onLogout={onLogout} />
        <main className="flex-1 flex items-center justify-center pt-20 lg:pt-0">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </main>
      </div>
    );
  }

  const percVendido = stats.totalUnidades > 0 ? Math.round((stats.unidadesVendidas / stats.totalUnidades) * 100) : 0;

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50">
      <Sidebar onLogout={onLogout} />
      
      <PlantaoModal 
        isOpen={showPlantaoModal} 
        onClose={() => setShowPlantaoModal(false)}
        currentUser={currentUser}
      />
      
      <main className="flex-1 p-4 lg:p-6 overflow-y-auto pt-20 lg:pt-6">
        <div className="w-full max-w-7xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="text-left">
              <h1 className="text-3xl font-bold text-slate-800 dark:text-white text-left" data-testid="dashboard-title">
                InstaIMOB CRM
              </h1>
              <p className="text-slate-500">
                Visão geral dos seus empreendimentos
              </p>
            </div>
            
            <div className="flex items-center gap-2 bg-white rounded-xl p-1.5 shadow-lg border border-slate-200">
              <CalendarDays className="w-4 h-4 text-slate-400 ml-2" />
              <div className="flex flex-wrap gap-1">
                {PERIODOS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setPeriodo(p.value)}
                    data-testid={`periodo-${p.value}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      periodo === p.value
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Avisos */}
          {avisos?.plantao?.mostrar_aviso && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-testid="aviso-plantao">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Configure o Plantão do Fim de Semana</p>
                <p className="text-sm text-amber-700 mt-1">{avisos.plantao.mensagem}</p>
              </div>
              <button onClick={() => setShowPlantaoModal(true)} className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium">
                Configurar
              </button>
            </div>
          )}
          
          {avisos?.rodizio?.mostrar_aviso && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3" data-testid="aviso-rodizio">
              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-blue-800">Configure o Rodízio de Hoje</p>
                <p className="text-sm text-blue-700 mt-1">{avisos.rodizio.mensagem}</p>
              </div>
              <a href="/equipe?tab=rodizio" className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
                Configurar
              </a>
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-testid="kpi-leads">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-blue-100 rounded-xl">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                  {PERIODOS.find(p => p.value === periodo)?.label}
                </span>
              </div>
              <p className="text-3xl font-bold text-slate-800 dark:text-white text-left">{stats.clientesNoPeriodo}</p>
              <p className="text-sm text-slate-500">Novos Leads</p>
              <p className="text-xs text-slate-400 mt-1">Total: {stats.totalClientes}</p>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-testid="kpi-empreendimentos">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-emerald-100 rounded-xl">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full font-medium">
                  {stats.totalUnidades} un.
                </span>
              </div>
              <p className="text-3xl font-bold text-slate-800 dark:text-white text-left">{stats.totalEmpreendimentos}</p>
              <p className="text-sm text-slate-500">Empreendimentos</p>
              <p className="text-xs text-slate-400 mt-1">{stats.totalUnidades} unidades no total</p>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-testid="kpi-vendas">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-purple-100 rounded-xl">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full font-medium">
                  {percVendido}% vendido
                </span>
              </div>
              <p className="text-xl font-bold text-slate-900">{formatCurrency(stats.vgvVendido)}</p>
              <p className="text-sm text-slate-500">VGV Comercializado</p>
              <p className="text-xs text-slate-400 mt-1">
                {stats.unidadesVendidas} vendidas | VGV Total: {formatCurrency(stats.vgvTotal)}
              </p>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-testid="kpi-negociacoes">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2.5 bg-amber-100 rounded-xl">
                  <Target className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-800 dark:text-white text-left">{stats.negociacoes}</p>
              <p className="text-sm text-slate-500">Em Negociação</p>
              <p className="text-xs text-slate-400 mt-1">No período selecionado</p>
            </div>
          </div>

          {/* Empreendimentos Cards */}
          {empreendimentos.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <HardHat className="w-5 h-5 text-slate-600" />
                Empreendimentos
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {empreendimentos.map(emp => {
                  const st = STATUS_EMPREENDIMENTO[emp.status] || STATUS_EMPREENDIMENTO.lancamento;
                  return (
                    <a
                      key={emp.id}
                      href={`/empreendimento/${emp.id}`}
                      className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200 hover:shadow-xl hover:border-slate-300 transition-all group"
                      data-testid={`emp-card-${emp.slug || emp.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate group-hover:text-primary transition-colors">
                            {emp.nome}
                          </h3>
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            <span className="text-xs text-slate-500 truncate">
                              {emp.bairro}{emp.cidade ? `, ${emp.cidade}` : ''}
                            </span>
                          </div>
                        </div>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.bgLight} ${st.textColor} whitespace-nowrap`}>
                          {st.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-base font-bold text-slate-900">{emp.total_unidades || 0}</p>
                          <p className="text-[10px] text-slate-500">Unidades</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-base font-bold text-slate-900">{emp.torres || '-'}</p>
                          <p className="text-[10px] text-slate-500">Torres</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2">
                          <p className="text-base font-bold text-slate-900">{emp.pavimentos || '-'}</p>
                          <p className="text-[10px] text-slate-500">Pavimentos</p>
                        </div>
                      </div>
                      {emp.previsao_entrega && (
                        <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> Previsão: {emp.previsao_entrega}
                        </p>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {/* Leads por Mês */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-5 shadow-lg border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">
                Novos Leads por Mês
                <span className="text-xs font-normal text-slate-500 ml-2">
                  ({PERIODOS.find(p => p.value === periodo)?.label})
                </span>
              </h3>
              <div className="h-64">
                {clientesPorMes.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <AreaChart data={clientesPorMes}>
                      <defs>
                        <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="mes" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                      <Area type="monotone" dataKey="quantidade" stroke="#3b82f6" fillOpacity={1} fill="url(#colorLeads)" name="Leads" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">Sem dados no período</div>
                )}
              </div>
            </div>

            {/* Unidades por Status */}
            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">
                Unidades por Status
              </h3>
              <div className="h-64">
                {unidadesPorStatus.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <PieChart>
                      <Pie data={unidadesPorStatus} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                        {unidadesPorStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm">
                    <Home className="w-8 h-8 mb-2 text-slate-300" />
                    <p>{stats.totalUnidades} unidades totais</p>
                    <p className="text-xs mt-1">Cadastre unidades para ver o gráfico</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            {/* Resumo de Hoje */}
            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">
                Resumo de Hoje
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-3xl font-bold text-slate-800 dark:text-white text-left">{stats.agendamentosHoje}</p>
                    <p className="text-sm text-slate-500">Visitas agendadas para hoje</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-3xl font-bold text-slate-800 dark:text-white text-left">{stats.negociacoes}</p>
                    <p className="text-sm text-slate-500">Negociações em andamento</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <Building2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-3xl font-bold text-slate-800 dark:text-white text-left">{stats.totalEmpreendimentos}</p>
                    <p className="text-sm text-slate-500">Empreendimentos ativos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ranking Corretores */}
            <div className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Ranking de Corretores
              </h3>
              <div className="space-y-3">
                {rankingCorretores.interacoes.length > 0 ? (
                  rankingCorretores.interacoes.map((corretor, idx) => (
                    <div key={corretor.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'bg-amber-400 text-amber-900' :
                        idx === 1 ? 'bg-slate-300 text-slate-700' :
                        idx === 2 ? 'bg-amber-600 text-amber-100' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{corretor.nome}</p>
                        <p className="text-xs text-slate-500">{corretor.clientes} clientes atribuídos</p>
                      </div>
                      <p className="text-sm font-semibold text-blue-600">
                        {corretor.interacoes} ações
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Nenhuma interação registrada</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
